import { useEffect } from 'react';

import {
  getResourceContentErrorMessage,
  getResourceContentTypeAccept,
  getResourceFileMaxSize,
  RESOURCE_FILE_UPLOAD_ERROR_CODE,
  type ResourceContentType,
} from 'src/data/performance';
import { getFileExtension } from 'src/lib/file';
import type { Callback } from 'src/lib/types';
// @ts-expect-error - useNotificationActions does not have types yet
import { useNotificationActions } from 'src/store/notification';

interface HookParams {
  resourceContentType: ResourceContentType;
  allowedFileTypes?: string[];
  onPaste: Callback<File[]>;
  disabled?: boolean;
}

export const useSupportPasteContentFile = ({
  resourceContentType,
  allowedFileTypes,
  onPaste,
  disabled,
}: HookParams) => {
  const notifications = useNotificationActions();

  useEffect(() => {
    if (disabled) {
      return;
    }

    const handler = async (e: ClipboardEvent) => {
      if (!e?.clipboardData?.files?.length) {
        return;
      }

      const pastedFile = e.clipboardData.files[0];
      const validationError = validatePastedFile({
        resourceContentType,
        allowedFileTypes,
        file: pastedFile,
      });
      if (validationError) {
        notifications.displayWarning(validationError);
        return;
      }

      onPaste([pastedFile]);
    };

    window.addEventListener('paste', handler);
    return () => {
      window.removeEventListener('paste', handler);
    };
  }, [resourceContentType, allowedFileTypes, onPaste, disabled, notifications]);
};

const validatePastedFile = ({
  resourceContentType,
  allowedFileTypes,
  file,
}: {
  resourceContentType: ResourceContentType;
  allowedFileTypes?: string[];
  file: File;
}) => {
  const acceptedMimeTypes = Object.keys(getResourceContentTypeAccept(resourceContentType));
  const maxFileSize = getResourceFileMaxSize(resourceContentType);

  if (!acceptedMimeTypes.includes(file.type)) {
    return getResourceContentErrorMessage(
      resourceContentType,
      RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType
    );
  }

  if (Array.isArray(allowedFileTypes) && allowedFileTypes.length > 0) {
    const extension = getFileExtension(file.name);
    if (!allowedFileTypes.includes(extension)) {
      return getResourceContentErrorMessage(
        resourceContentType,
        RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType
      );
    }
  }

  if (maxFileSize && file.size > maxFileSize) {
    return getResourceContentErrorMessage(
      resourceContentType,
      RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge
    );
  }

  return null;
};
